/**
 * Maps any thrown error to a plain-language message safe to show students.
 *
 * Rules:
 *  - NetworkError / 5xx / no status → generic "try again" message (never show a status code)
 *  - AuthError → sign-in-again message
 *  - SubscriptionRequiredError → subscription nudge
 *  - 4xx with a server-provided message → show it (login/validation copy is human-readable)
 *  - Anything else → generic fallback
 */
export function friendlyErrorMessage(error: unknown): string {
    if (!(error instanceof Error)) return 'Something went wrong. Please try again.'

    const apiError = error as { name?: string; status?: number }
    const name = apiError.name
    const status = apiError.status

    if (name === 'NetworkError' || !status || status === 0 || status >= 500)
        return "We're having trouble connecting right now. Please wait a moment and try again."

    if (name === 'AuthError')
        return 'Your session has expired. Please sign in again.'

    if (name === 'SubscriptionRequiredError')
        return 'This content requires a subscription.'

    if (status === 403)
        return "You don't have permission to do this."

    if (status === 404)
        return 'This item could not be found.'

    // 4xx: the server message is likely human-readable (e.g., "Email already in use").
    // Show it only if it doesn't look like a raw HTTP status line.
    const msg = error.message
    if (msg && !msg.startsWith('Request failed') && !/status \d{3}/.test(msg))
        return msg

    return 'Something went wrong. Please try again.'
}

/**
 * The kind of a load failure, so a page can show the RIGHT screen instead of a
 * misleading one (e.g. a dropped network must never read as "not found").
 *
 *  - offline   → device/connection can't reach the server → retry
 *  - server    → the server answered 5xx → retry
 *  - notfound  → the server answered 404 → the thing genuinely doesn't exist
 *  - auth      → session expired / 401 → sign in again
 *  - forbidden → 403 → no permission
 *  - unknown   → anything else (handled elsewhere or a real bug)
 *
 * Note: the API client tags true network failures as `NetworkError` (no status)
 * and 5xx as `ServerError` carrying its real `status`, even after retries.
 */
export type ErrorKind = 'offline' | 'server' | 'notfound' | 'auth' | 'forbidden' | 'unknown'

export function classifyError(error: unknown): ErrorKind {
    if (!(error instanceof Error)) return 'unknown'
    const { name, status } = error as { name?: string; status?: number }

    if (name === 'AuthError' || status === 401) return 'auth'
    if (status === 403) return 'forbidden'
    if (status === 404) return 'notfound'
    if (typeof status === 'number' && status >= 500) return 'server'
    // Only an error explicitly tagged NetworkError (api.ts does this for every true
    // fetch failure and the offline fail-fast path) counts as offline. A plain,
    // status-less Error thrown by app code (e.g. "No courses in stage") is a real
    // bug, not a dropped connection — it must fall to 'unknown', never 'offline'.
    if (name === 'NetworkError') return 'offline'
    return 'unknown'
}
